import { trackDocumentStructure } from "../tiptap/document_delivery_bridge"
import { buildPrintGuard } from "../tiptap/print_guard"
import { Controller } from "stimulus"
import { buildToolbar, updateToolbarState } from "../tiptap/toolbar"
import { openDocumentFind } from "../tiptap/find_replace"
import { revealSection } from "../tiptap/section_navigation"

// Orchestrates the sectioned document editor.
// Builds ONE shared toolbar, tracks which section has focus,
// aggregates word counts. AI text actions work via window.tiptapEditor.
// Loads sections sequentially (top-to-bottom) to prevent backfilling.
export default class extends Controller {
  static targets = ["sectionsList"]
  static values = { postId: String, postSlug: String }

  connect() {
    this.sectionControllers = new Map()
    this.activeEditor = null

    this._onRegistered = this.registerSection.bind(this)
    this._onUnregistered = event => {
      this.sectionControllers.delete(event.detail.sectionId)
      if (this.activeEditor === event.detail.editor) {
        this.activeEditor = null
        window.activeSectionEditor = null
        window.tiptapEditor = null
      }
      this.updateAggregateWordCount()
      document.dispatchEvent(new CustomEvent("tiptap:section-loaded"))
    }
    this._onFocused = this.handleSectionFocus.bind(this)
    this._onWordCount = this.updateAggregateWordCount.bind(this)

    this.element.addEventListener("section:registered", this._onRegistered)
    this.element.addEventListener("section:unregistered", this._onUnregistered)
    this.element.addEventListener("section:focused", this._onFocused)
    this.element.addEventListener("section:wordcount", this._onWordCount)

    this.buildSharedToolbar()
    this._printGuard = buildPrintGuard(document.getElementById("publishForm"), {
      getSections: () => Array.from(this.sectionControllers.values()),
      sync: () => this.sectionControllers.forEach(section => section.syncToField()),
      onStatus: message => this._toolbar?.setPrintStatus(message),
    })
    this.loadSectionsSequentially()

    // Keyboard shortcut for Find & Replace
    this._onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && this.activeEditor?.isFocused) {
        e.preventDefault()
        this.toolbarEl.querySelector('[data-cmd="link"]').click()
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
        e.preventDefault()
        this.showFindBar(e.shiftKey)
      }
    }
    document.addEventListener("keydown", this._onKeyDown)

    // Expose for TOC access
    window.sectionEditorControllers = this.sectionControllers
  }

  disconnect() {
    this.element.removeEventListener("section:registered", this._onRegistered)
    this.element.removeEventListener("section:unregistered", this._onUnregistered)
    this.element.removeEventListener("section:focused", this._onFocused)
    this.element.removeEventListener("section:wordcount", this._onWordCount)
    document.removeEventListener("keydown", this._onKeyDown)
    if (this._printGuard) this._printGuard.destroy()
    if (this._toolbar) this._toolbar.destroy()
    if (this._findBar) this._findBar.close()
    this._frameQueue = []
    this.sectionControllers.clear()
    this.activeEditor = null
    window.activeSectionEditor = null
    window.tiptapEditor = null
    window.sectionEditorControllers = null
  }

  getEditors() {
    return Array.from(this.sectionControllers.values())
      .map(ctrl => ctrl.editor)
      .filter(Boolean)
  }

  showFindBar(withReplace = false) {
    const container = this.element.closest(".left-column") || this.element
    this._findBar = openDocumentFind(container, () => this.getEditors(), { showReplace: withReplace })
  }

  buildSharedToolbar() {
    const editorContent = this.element.closest(".editor-content")
    if (!editorContent) return

    const getEditor = () => this.activeEditor
    this._toolbar = buildToolbar(editorContent, getEditor, { beforePrint: () => this._printGuard ? this._printGuard.check() : true, onFind: () => this.showFindBar() })
    this.toolbarEl = this._toolbar.toolbar
  }

  // Load Turbo Frames one at a time, top to bottom
  loadSectionsSequentially() {
    const frames = this.element.querySelectorAll("turbo-frame[data-section-src]")
    this._frameQueue = Array.from(frames)
    this._loadNextFrame()
  }

  _loadNextFrame() {
    if (!this._frameQueue || this._frameQueue.length === 0) return

    const frame = this._frameQueue.shift()
    const src = frame.dataset.sectionSrc
    if (!src) { this._loadNextFrame(); return }

    let advanced = false
    const advance = () => {
      if (advanced) return
      advanced = true
      frame.removeEventListener("turbo:frame-load", advance)
      clearTimeout(timeoutId)
      setTimeout(() => this._loadNextFrame(), 50)
    }

    frame.addEventListener("turbo:frame-load", advance)
    // Safety timeout — don't let a failed load stall the whole chain
    const timeoutId = setTimeout(advance, 5000)

    frame.setAttribute("src", src)
  }

  registerSection(event) {
    const { sectionId, controller } = event.detail
    this.sectionControllers.set(sectionId, controller)
    // Notify TOC panel that a new section is available
    document.dispatchEvent(new CustomEvent("tiptap:section-loaded"))
  }

  handleSectionFocus(event) {
    const { sectionId, editor } = event.detail
    this.activeEditor = editor
    window.activeSectionEditor = editor
    window.tiptapEditor = editor

    if (this.toolbarEl) {
      updateToolbarState(this.toolbarEl, editor)
    }
  }

  updateAggregateWordCount() {
    let total = 0
    this.sectionControllers.forEach((ctrl) => {
      total += ctrl.wordCount || 0
    })
    const wc = document.getElementById("wordCount")
    if (wc) wc.textContent = total.toLocaleString()
    const wc2 = document.getElementById("wordsCount")
    if (wc2) wc2.textContent = total.toLocaleString()
  }

  addSection() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    return trackDocumentStructure("Adding a section", () => fetch(`/posts/${this.postSlugValue}/sections`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json",
      },
      credentials: "same-origin",
    })
    .then(res => res.json())
    .then(data => {
      if (data.status === "success" && data.html) {
        const list = this.sectionsListTarget
        list.insertAdjacentHTML("beforeend", data.html)
        const newFrame = list.lastElementChild
        if (newFrame) {
          setTimeout(() => {
            revealSection(newFrame)
          }, 300)
        }
      }
    })
    .catch(err => console.error("Failed to add section:", err)))
  }
}
